import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useBusinessTheme } from './BusinessThemeContext';

export enum OnboardingStep {
  BusinessSettings = 'BusinessSettings',
  Roles = 'Roles',
  Users = 'Users',
  AppointmentTypes = 'AppointmentTypes',
  Calendar = 'Calendar',
  Completed = 'Completed'
}

interface OnboardingState {
  userId: string;
  currentStep: OnboardingStep;
  isCompleted: boolean;
}

interface OnboardingContextType {
  isOnboarding: boolean;
  currentStep: OnboardingStep;
  nextStep: () => void;
  completeOnboarding: () => void;
  skipOnboarding: () => void;
  isAdminUser: boolean;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

interface OnboardingProviderProps {
  children: ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children }) => {
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(OnboardingStep.BusinessSettings);
  const { isAuthenticated, user, token } = useAuth();
  const navigate = useNavigate();
  
  // Determine if user is an admin
  const isAdminUser = user?.isAdmin || user?.businessRoles?.some(role => role.isBusinessAdmin) || false;

  // Reset onboarding state when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setIsOnboarding(false);
      setCurrentStep(OnboardingStep.BusinessSettings);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Only check onboarding status when authenticated and token exists
    console.log('checkOnboardingStatus', isAuthenticated, token);
    if (isAuthenticated && token) {
      checkOnboardingStatus();
    }
  }, [isAuthenticated, token]);

  const checkOnboardingStatus = async () => {
    try {
      // Make sure to include the authorization header
      const shouldShowResponse = await axios.get('/api/onboarding/should-show', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setIsOnboarding(shouldShowResponse.data);
      
      if (shouldShowResponse.data) {
        // Also include token in this request
        const stateResponse = await axios.get('/api/onboarding/state', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        let initialStep = stateResponse.data.currentStep;
        
        // For non-admin users, skip admin-only steps
        if (!isAdminUser && 
            (initialStep === OnboardingStep.BusinessSettings || 
             initialStep === OnboardingStep.Roles || 
             initialStep === OnboardingStep.Users)) {
          initialStep = OnboardingStep.AppointmentTypes;
          await axios.post('/api/onboarding/update-step', { step: initialStep }, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        }
        
        setCurrentStep(initialStep);
        
        // Navigate to the appropriate page based on the current step
        navigateToStep(initialStep);
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      // Don't show onboarding if there's an error
      setIsOnboarding(false);
    }
  };

  const navigateToStep = (step: OnboardingStep) => {
    switch (step) {
      case OnboardingStep.BusinessSettings:
        navigate('/business-settings');
        break;
      case OnboardingStep.Roles:
        navigate('/business-settings?tab=roles');
        break;
      case OnboardingStep.Users:
        navigate('/business-settings?tab=users');
        break;
      case OnboardingStep.AppointmentTypes:
        navigate('/business-settings?tab=appointment-types');
        break;
      case OnboardingStep.Calendar:
        navigate('/calendar');
        break;
      default:
        break;
    }
  };

  const nextStep = async () => {
    let nextStep: OnboardingStep;
    
    // Determine next step based on current step and user role
    switch (currentStep) {
      case OnboardingStep.BusinessSettings:
        nextStep = isAdminUser ? OnboardingStep.Roles : OnboardingStep.AppointmentTypes;
        break;
      case OnboardingStep.Roles:
        nextStep = isAdminUser ? OnboardingStep.Users : OnboardingStep.AppointmentTypes;
        break;
      case OnboardingStep.Users:
        nextStep = OnboardingStep.AppointmentTypes;
        break;
      case OnboardingStep.AppointmentTypes:
        nextStep = OnboardingStep.Calendar;
        break;
      case OnboardingStep.Calendar:
        nextStep = OnboardingStep.Completed;
        break;
      default:
        nextStep = OnboardingStep.Completed;
    }

    try {
      // Fix: Send the enum value properly wrapped in an object
      await axios.post('/api/onboarding/update-step', { step: nextStep }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setCurrentStep(nextStep);
      
      if (nextStep === OnboardingStep.Completed) {
        completeOnboarding();
      } else {
        navigateToStep(nextStep);
      }
    } catch (error) {
      console.error('Error updating onboarding step:', error);
    }
  };

  const completeOnboarding = async () => {
    try {
      // Include authorization header
      await axios.post('/api/onboarding/complete', {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setIsOnboarding(false);
      setCurrentStep(OnboardingStep.Completed);
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  };

  const skipOnboarding = async () => {
    try {
      // Include authorization header
      await axios.post('/api/onboarding/complete', {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setIsOnboarding(false);
      navigate('/calendar');
    } catch (error) {
      console.error('Error skipping onboarding:', error);
    }
  };

  return (
    <OnboardingContext.Provider value={{ 
      isOnboarding, 
      currentStep, 
      nextStep, 
      completeOnboarding,
      skipOnboarding,
      isAdminUser
    }}>
      {children}
    </OnboardingContext.Provider>
  );
}; 